/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

// Component imports
import { Card, Col, Empty, Grid, Row, Skeleton, Space, Typography } from 'antd';
import { FallOutlined, LineOutlined, RiseOutlined } from '@ant-design/icons';
import ChartCard from '../components/Charts/ChartCard';
import DocumentTitle from '../components/DocumentTitle';
import BotAvatar from '../components/UI/BotAvatar';
import DashboardTotalSessionsSection from '../components/Analytics/DashboardTotalSessionsSection';
import DashboardTotalUsersSection from '../components/Analytics/DashboardTotalUsersSection';
import BoxIcon from '../components/Icons/BoxIcon';

// Misc imports
import { ADDITIONAL_ROLES_ORG, PERMISSIONS_ORG } from '../constants/main';
import { NAV_BOT, NAV_BOTS, NAV_LIVECHAT } from '../constants/navigation';
import { useFetchDashboardQuery } from '../store/dashboard/dashboardApi';
import { useFetchOrganizationQuery } from '../store/organization/organizationApi';
import { getCurrentQueryState } from '../store/utils/getCurrentQueryState';
import { isTokenExpired } from '../utils/auth/isTokenExpired';
import { formatNumberToThousandSeparator } from '../utils/string/formatNumberToThousandSeparator';
import useUserPermissions from '../utils/hooks/useUserPermissions';
import replaceUrlParams from '../utils/string/replaceUrlParams';
import is from '../utils/is';
import FooterContent from '../components/FooterContent';

// Constant declarations
const { Text } = Typography;
const { useBreakpoint } = Grid;
const STATS_PERIOD_FORMAT = 'DD MMM YYYY';

const DashboardScreen = () => {
  const { t } = useTranslation();
  const { orgId } = useParams();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const userPermissions = useUserPermissions(orgId, null);

  const { token } = useSelector((state) => state.auth);
  const { currentUser } = useSelector((state) => state.auth);
  const { currentData: dashboard, isFetching: isLoadingDashboard, isError: fetchDashboardError } = useFetchDashboardQuery({ orgId }, { skip: (
    !orgId
    || !token
    || isTokenExpired(token)
    || !(is.array(userPermissions) && !is.emptyArray(userPermissions) && userPermissions.some(permission => Object.values(PERMISSIONS_ORG).includes(permission)))
  ) });
  const { data: bots, isFetching: isLoadingBots, isError: fetchBotsError } = getCurrentQueryState('fetchBots', { orgId }, !orgId || !token);

  // Only fetch the organization info if the user has at least one level of org access
  const { data: organization } = useFetchOrganizationQuery({ orgId }, {
    skip: !(is.array(userPermissions) && !is.emptyArray(userPermissions) && userPermissions.some(permission => Object.values(PERMISSIONS_ORG).includes(permission)))
  });

  const isOnlyLiveAgent = currentUser.effectiveRoles?.organizationRoles[orgId]?.length === 1 && currentUser.effectiveRoles?.organizationRoles[orgId][0] === ADDITIONAL_ROLES_ORG.LIVE_AGENT;

  // Redirect to the livechat screen if the user has only the live agent role
  useEffect(() => {
    if (isOnlyLiveAgent) {
      navigate(replaceUrlParams(NAV_LIVECHAT, { orgId }));
    }
  }, []);

  const renderTotalSessionsChart = () => {
    const sessionsInfo = dashboard?.totalSessions || {};
    const sessionsData = sessionsInfo.sessionsGraph || [];
    const chartData = [];

    if (sessionsData && sessionsData.length) {
      for (let i = 0; i < sessionsData.length; i += 1) {
        chartData.push({
          x: sessionsData[i].date,
          y: sessionsData[i].value
        });
      }
    }

    return (
      <ChartCard
        title={t('Sessions')}
        icon={<BoxIcon name="chat" />}
        total={sessionsInfo.totalSessions}
        trend={sessionsInfo.totalSessionsTrend * 100}
        className="h-100"
        loading={!(dashboard && bots && !isLoadingDashboard)}
        startDate={sessionsData[0]?.date}
        endDate={sessionsData[sessionsData.length - 1]?.date}
        // The footer here is being used to align both charts in the same exact way in smaller screens
        footer={
          <div style={{ height: 100 }} className="visibility-hidden" />
        }
      >
        <DashboardTotalSessionsSection
          data={chartData}
          loading={isLoadingDashboard}
        />
      </ChartCard>
    );
  };

  const renderTotalUsersChart = () => {
    const totalUsersInfo = dashboard?.totalUsers || {};
    const usersData = totalUsersInfo.totalUsersGraph || [];
    const chartData = [];

    if (usersData && usersData.length) {
      for (let i = 0; i < usersData.length; i +=1) {
        chartData.push({
          x: usersData[i].date,
          y: usersData[i].value
        });
      }
    }

    return (
      <ChartCard
        title={t('Users')}
        icon={<BoxIcon name="group" />}
        total={totalUsersInfo?.totalUsers}
        trend={totalUsersInfo?.totalUsersTrend * 100}
        className="h-100"
        startDate={usersData[0]?.date}
        endDate={usersData[usersData.length - 1]?.date}
        loading={!(dashboard && bots && !isLoadingDashboard)}
        footer={
          <Row align="middle" className="mb-2 d-flex align-content-end" gutter={[8, 8]} style={{ height: 100 }}>
            <Col flex="auto">
              <Space size="middle">
                <Text className="fs-base">
                  <Trans t={t}>New <br/> Users</Trans>
                </Text>

                <Text className="fs-xxl fw-bold">
                  {formatNumberToThousandSeparator(totalUsersInfo?.newUsers)}
                </Text>
              </Space>
            </Col>

            <Col flex="none">
              <Space size="middle">
                <Text className="fs-base">
                  <Trans t={t}>Returning <br/> Users</Trans>
                </Text>

                <Text className="fs-xxl fw-bold">
                  {formatNumberToThousandSeparator(totalUsersInfo?.returningUsers)}
                </Text>
              </Space>
            </Col>
          </Row>
        }
      >
        <DashboardTotalUsersSection
          data={chartData}
          loading={isLoadingDashboard}
        />
      </ChartCard>
    );
  };

  const renderMostUsedBotsCard = () => {
    if (fetchBotsError) {
      return (
        <Card className="h-100 border-0">
          <Empty
            description={<Text type="secondary">{t('Failed to fetch agents')}</Text>}
            className="mb-4"
          />
        </Card>
      );
    }

    if (dashboard?.mostUsedTenants?.length === 0) {
      return (
        <Card className="h-100 border-0">
          <Empty
            description={<Text type="secondary">{t('No agents available')}</Text>}
            className="mb-4"
          />
        </Card>
      );
    }

    // Else
    return (
      <Card className="h-100 border-0">
        <Row justify="space-between" className="mb-4">
          <Col>
            <Typography.Title level={5} className="fw-bolder">
              {t('Most Used Agents')}
            </Typography.Title>
          </Col>

          <Col>
            <Link to={replaceUrlParams(NAV_BOTS, { orgId })} className="fw-bold pr-2">{t('View All')}</Link>
          </Col>
        </Row>

        {
          dashboard?.mostUsedTenants ? (
            <Row
              justify="start"
              gutter={[16, 16]}
              className="overflow-y-scroll scrollbar-custom scrollbar-custom-nohide"
              style={{ maxHeight: 350 }}
            >
              {
                (dashboard.mostUsedTenants || []).slice(0, 6).map((bot) => (
                  <Col
                    key={bot.tenantId}
                    style={{ width: (screens.xl || screens.lg) ? '33.33%' : screens.md ? '50%' : '100%' }}
                  >
                    <Link to={replaceUrlParams(NAV_BOT, { tenantId: bot.tenantId, orgId })}>
                      <Card
                        className="bg-layout shadow-none p-0 cursor-pointer bg-hover-layout"
                        bordered={false}
                      >
                        <Row justify="space-between" align="middle" wrap={false} className="p-4" gutter={[12, 12]}>
                          <Col flex="auto">
                            <BotAvatar
                              bot={bot}
                              avatarSize={24}
                              ellipsis={{ rows: 2, expandable: false, tooltip: bot.displayName }}
                            />
                          </Col>

                          <Col flex="none">
                            <Space direction="vertical" size={0}>
                              <div>
                                <Typography.Text type="secondary">
                                  <BoxIcon name="group" /> {formatNumberToThousandSeparator(bot.totalUsers)}
                                </Typography.Text>

                                {
                                  bot.totalUsersTrend && bot.totalUsersTrend !== 0 ? (
                                    bot.totalUsersTrend > 0 ? (
                                      <RiseOutlined className="ml-2 text-success" />
                                    ) : (
                                      <FallOutlined className="ml-2 text-error" />
                                    )
                                  ) : (
                                    <LineOutlined className="ml-2 text-secondary" />
                                  )
                                }
                              </div>

                              <div>
                                <Typography.Text type="secondary">
                                  <BoxIcon name="chat" /> {formatNumberToThousandSeparator(bot.totalSessions)}
                                </Typography.Text>

                                {
                                  bot.totalSessionsTrend && bot.totalSessionsTrend !== 0 ? (
                                    bot.totalSessionsTrend > 0 ? (
                                      <RiseOutlined className="ml-2 text-success" />
                                    ) : (
                                      <FallOutlined className="ml-2 text-error" />
                                    )
                                  ) : (
                                    <LineOutlined className="ml-2 text-secondary" />
                                  )
                                }
                              </div>
                            </Space>
                          </Col>
                        </Row>
                      </Card>
                    </Link>
                  </Col>
                ))
              }
            </Row>
          ) : isLoadingBots ? (
            <Row justify="start" align="middle" className="h-100" gutter={[16, 16]}>
              {
                [...Array(6).keys()].map((index) => (
                  <Col key={index} style={{ width: screens.lg ? '33.33%' : screens.md ? '50%' : '100%' }}>
                    <Card className="bg-layout shadow-none p-0" bordered={false}>
                      <Row justify="space-between" align="middle" wrap={false} className="px-4 py-7" gutter={[12, 12]}>
                        <Col flex="auto">
                          <Skeleton.Avatar active size={24} shape="circle" />
                          <Skeleton.Button active size="small" className="ml-2 w-70" />
                        </Col>

                        <Col flex="none">
                          <Skeleton.Button active size="small" style={{ width: 50 }} />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                ))
              }
            </Row>
          ) : null
        }
      </Card>
    );
  };

  const renderDashboard = () => {
    if (fetchDashboardError) {
      return (
        <Col xs={24}>
          <Card>
            <Empty
              description={<Text type="secondary">{t('There is no dashboard available for this user. If this is an error, please contact an administrator.')}</Text>}
            />
          </Card>
        </Col>
      );
    }

    // Else
    return (
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          {renderMostUsedBotsCard()}
        </Col>

        <Col xs={24} lg={12}>
          {renderTotalSessionsChart()}
        </Col>

        <Col xs={24} lg={12}>
          {renderTotalUsersChart()}
        </Col>
      </Row>
    );
  };

  return !isOnlyLiveAgent && (
    <Fragment>
      <DocumentTitle title={t('Overview')} />

      <Row gutter={[24, 24]}>
        <Col span={24}>
          {renderDashboard()}
        </Col>

        {
          dashboard && organization ? (
            <FooterContent content={
              <Col span={24}>
                {
                  dashboard?.period?.start && dashboard?.period?.end ? (
                    <Text>
                      <strong>{t('Stats period')}:</strong> {t('from')} {moment.tz(moment(dashboard.period.start), organization?.timezone).format(STATS_PERIOD_FORMAT)} {t('to')} {moment.tz(moment(dashboard.period.end), organization?.timezone).format(STATS_PERIOD_FORMAT)} | <strong>{t('Timezone')}:</strong> {organization?.timezone ?? `(${t('Timezone not available')})`}
                    </Text>
                  ) : (
                    <Text>{t('Stats period not available')}</Text>
                  )
                }
              </Col>
            }
            />
          ) : null
        }
      </Row>
    </Fragment>
  );
};

export default DashboardScreen;
